/*
  Communite UI core
  Accessible modal/drawer and status-message behavior with Shopify editor lifecycle support.
*/

(() => {
  const ROOT_SELECTOR = '[data-cm-dialog-scope]'
  const DIALOG_SELECTOR = '[data-cm-dialog]'
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',')
  const controllers = new WeakMap()

  const findRoots = (root) => {
    const roots = []

    if (root instanceof Element && root.matches(ROOT_SELECTOR)) {
      roots.push(root)
    }

    root.querySelectorAll?.(ROOT_SELECTOR).forEach(element => roots.push(element))
    return roots
  }

  const getFocusableElements = (element) => {
    return [...element.querySelectorAll(FOCUSABLE_SELECTOR)]
      .filter(control => !control.hidden && control.getAttribute('aria-hidden') !== 'true')
  }

  const syncDocumentLock = () => {
    const hasOpenDialog = Boolean(document.querySelector(`${DIALOG_SELECTOR}:not([hidden])`))
    document.documentElement.classList.toggle('cm-dialog-is-open', hasOpenDialog)
  }

  class CommuniteDialogController {
    constructor (root) {
      this.root = root
      this.abortController = new AbortController()
      this.signal = this.abortController.signal
      this.backdrop = root.querySelector('[data-cm-dialog-backdrop]')
      this.activeDialog = null
      this.activeOpener = null

      this.root.addEventListener('click', event => this.handleClick(event), { signal: this.signal })
      document.addEventListener('keydown', event => this.handleKeydown(event), { signal: this.signal })
      document.addEventListener('cm:dialog:before-open', event => {
        if (event.detail?.controller !== this) this.close(false)
      }, { signal: this.signal })
    }

    handleClick (event) {
      const opener = event.target.closest('[data-cm-dialog-open]')

      if (opener && this.root.contains(opener)) {
        event.preventDefault()
        this.open(opener.dataset.cmDialogOpen, opener)
        return
      }

      const closeControl = event.target.closest('[data-cm-dialog-close]')

      if (closeControl && this.root.contains(closeControl)) {
        event.preventDefault()
        this.close()
        return
      }

      const dismissControl = event.target.closest('[data-cm-status-dismiss]')

      if (dismissControl && this.root.contains(dismissControl)) {
        dismissControl.closest('[data-cm-status]')?.setAttribute('hidden', 'hidden')
        return
      }

      if (event.target === this.backdrop) this.close()
    }

    handleKeydown (event) {
      if (!this.activeDialog) return

      if (event.key === 'Escape') {
        event.preventDefault()
        this.close()
        return
      }

      if (event.key !== 'Tab') return

      const focusableElements = getFocusableElements(this.activeDialog)

      if (!focusableElements.length) {
        event.preventDefault()
        this.activeDialog.focus()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    findDialog (dialogName) {
      return [...this.root.querySelectorAll(DIALOG_SELECTOR)]
        .find(dialog => dialog.dataset.cmDialog === dialogName)
    }

    open (dialogName, opener) {
      const dialog = this.findDialog(dialogName)

      if (!dialog) return

      document.dispatchEvent(new CustomEvent('cm:dialog:before-open', {
        detail: { controller: this }
      }))

      this.close(false)
      this.activeDialog = dialog
      this.activeOpener = opener
      this.backdrop?.removeAttribute('hidden')
      dialog.removeAttribute('hidden')
      dialog.setAttribute('aria-hidden', 'false')
      opener?.setAttribute('aria-expanded', 'true')
      syncDocumentLock()

      const focusTarget = getFocusableElements(dialog)[0] || dialog
      window.requestAnimationFrame(() => focusTarget.focus())
      dialog.dispatchEvent(new CustomEvent('cm:dialog:open', { bubbles: true }))
    }

    close (restoreFocus = true) {
      if (!this.activeDialog) return

      const dialog = this.activeDialog
      const opener = this.activeOpener

      dialog.setAttribute('hidden', 'hidden')
      dialog.setAttribute('aria-hidden', 'true')
      this.backdrop?.setAttribute('hidden', 'hidden')
      opener?.setAttribute('aria-expanded', 'false')
      this.activeDialog = null
      this.activeOpener = null
      syncDocumentLock()
      dialog.dispatchEvent(new CustomEvent('cm:dialog:close', { bubbles: true }))

      if (restoreFocus && opener?.isConnected) {
        window.requestAnimationFrame(() => opener.focus())
      }
    }

    destroy () {
      this.close(false)
      this.abortController.abort()
      controllers.delete(this.root)
    }
  }

  const init = (root = document) => {
    findRoots(root).forEach(element => {
      if (controllers.has(element)) return

      controllers.set(element, new CommuniteDialogController(element))
    })
  }

  const destroy = (root) => {
    findRoots(root).forEach(element => controllers.get(element)?.destroy())
  }

  const announce = (message, type = 'info', root = document) => {
    const status = root.querySelector?.('[data-cm-status-live]')

    if (!status) return false

    const allowedTypes = ['info', 'success', 'warning', 'error']
    const statusType = allowedTypes.includes(type) ? type : 'info'
    const messageElement = status.querySelector('[data-cm-status-text]')

    status.classList.remove('cm-status--success', 'cm-status--warning', 'cm-status--error')
    if (statusType !== 'info') status.classList.add(`cm-status--${statusType}`)
    status.setAttribute('role', statusType === 'error' ? 'alert' : 'status')
    status.setAttribute('aria-live', statusType === 'error' ? 'assertive' : 'polite')
    status.removeAttribute('hidden')

    if (messageElement) messageElement.textContent = message
    else status.textContent = message

    return true
  }

  if (!window.CommuniteUI) {
    window.CommuniteUI = { init, destroy, announce }

    document.addEventListener('shopify:section:load', event => init(event.target))
    document.addEventListener('shopify:section:unload', event => destroy(event.target))
    document.addEventListener('shopify:section:select', event => init(event.target))
    document.addEventListener('shopify:block:select', event => {
      const root = event.target.closest?.(ROOT_SELECTOR)
      if (root) init(root)
    })
  }

  window.CommuniteUI.init(document)
})()
